// qr-scanner-modal.component.ts - COMPLETE FIXED VERSION WITH iOS SUPPORT
import {
  Component,
  Input,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  NgZone,
} from '@angular/core';
import { ModalController, Platform } from '@ionic/angular';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { NotFoundException } from '@zxing/library';
import { LoadingService } from '../../services/loading.service';
import { CameraPermissionService } from '../../services/camera-permission.service';

@Component({
  selector: 'app-qr-scanner-modal',
  templateUrl: './qr-scanner-modal.component.html',
  styleUrls: ['./qr-scanner-modal.component.scss'],
})
export class QrScannerModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() job: any;
  @Input() deviceId?: string;

  @ViewChild('videoPreview', { static: false })
  videoElement!: ElementRef<HTMLVideoElement>;

  scanning = false;
  cameraReady = false;
  errorMessage = '';

  codeReader: BrowserMultiFormatReader;
  canvas!: HTMLCanvasElement;
  context!: CanvasRenderingContext2D;
  currentStream: MediaStream | null = null;
  animationFrame: number | null = null;
  isInitializing = false;
  hasSuccessfullyScanned = false;
  isDestroyed = false;
  retryAttempts = 0;
  private readonly MAX_RETRY_ATTEMPTS = 3;
  private readonly MAX_CONSECUTIVE_FAILURES = 20;
  private readonly INIT_DELAY_MS = 300;
  private readonly IOS_INIT_DELAY_MS = 500; // Extra delay for iOS

  private loading: any;
  private isIOS: boolean = false;

  constructor(
    private modalCtrl: ModalController,
    private loadingService: LoadingService,
    private ngZone: NgZone,
    private cameraPermission: CameraPermissionService,
    private platform: Platform
  ) {
    this.codeReader = new BrowserMultiFormatReader();
    this.isIOS = this.platform.is('ios');
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    // iOS needs extra time to settle after modal opens
    const initDelay = this.isIOS ? this.IOS_INIT_DELAY_MS : this.INIT_DELAY_MS;
    
    setTimeout(async () => {
      if (!this.isDestroyed) {
        this.setupVideoElement();
        await this.initializeScanner();
      }
    }, initDelay);
  }

  ngOnDestroy(): void {
    console.log('[QrScanner] ngOnDestroy called');
    this.isDestroyed = true;
    this.completeCleanup();
  }

private setupVideoElement(): void {
  if (!this.videoElement?.nativeElement) return;
  
  const video = this.videoElement.nativeElement;
  
  // KEEP ALL YOUR EXISTING CODE, JUST ADD iOS-specific at the end:
  video.removeAttribute('controls');
  video.controls = false;
  video.removeAttribute('controlsList');
  video.setAttribute('playsinline', 'true'); 
  video.muted = true;
  video.autoplay = true;
  
  // iOS-specific additions:
  if (this.isIOS) {
    video.setAttribute('webkit-playsinline', 'true');
    video.setAttribute('x-webkit-airplay', 'deny');
    
    // Disable context menu
    video.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      return false;
    }, { passive: false });
    
    // Prevent multi-touch
    video.addEventListener('touchstart', (e) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    }, { passive: false });
  }
}

  private async initializeScanner(): Promise<void> {
    if (this.isInitializing || this.isDestroyed) {
      console.log('[QrScanner] Already initializing or destroyed');
      return;
    }
    
    this.isInitializing = true;
    await this.showLoading();

    try {
      console.log('[QrScanner] Starting scanner initialization...');
      
      let selectedDeviceId = this.deviceId;
      if (!selectedDeviceId) {
        selectedDeviceId = await this.cameraPermission.getBestCameraDevice();
      }

      if (!selectedDeviceId) {
        this.showInlineError('No camera device available on this device.');
        return;
      }

      if (this.isDestroyed) return;

      await this.startScanner(selectedDeviceId);
    } catch (error: any) {
      console.error('[QrScanner] Initialization failed:', error);
      if (!this.isDestroyed) {
        this.showInlineError(error.message || 'Failed to start camera.');
        if (error.name === 'NotAllowedError') {
          await this.handlePermissionDenied();
        }
      }
    } finally {
      this.isInitializing = false;
      await this.hideLoading();
    }
  }

  private async startScanner(deviceId: string): Promise<void> {
    this.scanning = false;
    this.cameraReady = false;
    this.errorMessage = '';

    try {
      console.log('[QrScanner] Starting video stream...');
      await this.startVideoStreamWithRetry(deviceId);

      if (this.isDestroyed) return;

      this.canvas = document.createElement('canvas');
      this.context = this.canvas.getContext('2d')!;

      console.log('[QrScanner] Starting live scanning...');
      this.startEnhancedLiveScanning();

      this.ngZone.run(() => {
        this.cameraReady = true;
        this.scanning = true;
        console.log('[QrScanner] Scanner ready!');
      });

      this.retryAttempts = 0;
      this.deviceId = deviceId;
    } catch (error: any) {
      console.error('[QrScanner] Failed to start scanner:', error);

      if (this.isDestroyed) return;

      this.retryAttempts++;
      if (this.retryAttempts < this.MAX_RETRY_ATTEMPTS) {
        console.log(`[QrScanner] Retry attempt ${this.retryAttempts}/${this.MAX_RETRY_ATTEMPTS}`);
        setTimeout(() => {
          if (!this.isDestroyed) {
            this.startScanner(deviceId);
          }
        }, 2000);
        return;
      }

      this.errorMessage = this.getErrorMessage(error);
      this.stopScanner();

      if (!this.isDestroyed) {
        this.modalCtrl.dismiss({ error: this.errorMessage });
      }
    }
  }

  private async startVideoStreamWithRetry(
    deviceId?: string,
    attempt = 0
  ): Promise<void> {
    if (!this.videoElement?.nativeElement) {
      throw new Error('Video element not available in DOM');
    }

    if (!deviceId) {
      deviceId = await this.cameraPermission.getBestCameraDevice();
    }

    await this.stopCurrentStream();

    const strategies: Array<{ name: string; constraints: MediaStreamConstraints }> = [];

    if (deviceId) {
      strategies.push({
        name: 'Exact deviceId with ideal resolution',
        constraints: {
          video: {
            deviceId: { exact: deviceId },
            width: { ideal: 1280, min: 320 },
            height: { ideal: 720, min: 240 },
          },
        },
      });

      strategies.push({
        name: 'Exact deviceId with minimal constraints',
        constraints: {
          video: {
            deviceId: { exact: deviceId },
            width: { min: 320 },
            height: { min: 240 },
          },
        },
      });
    }

    strategies.push({
      name: 'Environment facing mode',
      constraints: {
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      },
    });

    strategies.push({
      name: 'Basic video',
      constraints: { video: true },
    });

    let lastError: any;

    for (const strategy of strategies) {
      if (this.isDestroyed) {
        throw new Error('Component destroyed during stream start');
      }

      try {
        console.log(`[QrScanner] Trying strategy: "${strategy.name}"`);
        this.currentStream = await navigator.mediaDevices.getUserMedia(strategy.constraints);

        if (this.isDestroyed) {
          await this.stopCurrentStream();
          throw new Error('Component destroyed');
        }

        const video = this.videoElement.nativeElement;
        video.srcObject = this.currentStream;

        // Re-apply critical attributes after setting srcObject
        this.setupVideoElement();

        try {
          // iOS sometimes needs a small delay before play()
          if (this.isIOS) {
            await this.delay(100);
          }
          
          await video.play();
          console.log(`[QrScanner] ✓ Strategy "${strategy.name}" succeeded`);
          
          // Verify video is actually playing
          if (this.isIOS) {
            await this.delay(200);
            if (video.paused) {
              console.warn('[QrScanner] Video paused after play() - attempting replay');
              await video.play();
            }
          }
          
          return;
        } catch (playError: any) {
          console.error('[QrScanner] Video play failed:', playError);
          await this.stopCurrentStream();
          throw playError;
        }
      } catch (err: any) {
        console.warn(`[QrScanner] Strategy "${strategy.name}" failed:`, err.name || err);
        lastError = err;
        await this.stopCurrentStream();
      }
    }

    if (attempt < 2 && !this.isDestroyed) {
      console.log(`[QrScanner] All strategies failed, retrying (attempt ${attempt + 1}/2)...`);
      await this.delay(1000);
      return this.startVideoStreamWithRetry(deviceId, attempt + 1);
    }

    throw lastError || new Error('All video stream strategies failed');
  }

  private async stopCurrentStream(): Promise<void> {
    if (this.currentStream) {
      try {
        this.currentStream.getTracks().forEach(track => {
          track.stop();
          track.enabled = false;
        });
        this.currentStream = null;
        console.log('[QrScanner] Stream stopped');
      } catch (error) {
        console.error('[QrScanner] Error stopping stream:', error);
      }
    }

    if (this.videoElement?.nativeElement) {
      const video = this.videoElement.nativeElement;
      try {
        video.pause();
        video.srcObject = null;
      } catch (error) {
        console.error('[QrScanner] Error clearing video:', error);
      }
    }
  }

  private startEnhancedLiveScanning(): void {
    const video = this.videoElement?.nativeElement;
    if (!video) {
      console.error('[QrScanner] Video element not available');
      return;
    }

    let consecutiveFailures = 0;

    const scanFrame = async () => {
      if (this.isDestroyed || this.hasSuccessfullyScanned) {
        console.log('[QrScanner] Scan loop ended');
        return;
      }

      try {
        if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          if (this.canvas.width !== video.videoWidth || this.canvas.height !== video.videoHeight) {
            this.canvas.width = video.videoWidth;
            this.canvas.height = video.videoHeight;
          }

          this.context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);

          try {
            const result = await this.codeReader.decodeFromCanvas(this.canvas);
            if (result?.getText()) {
              console.log('[QrScanner] ✓ QR Code detected:', result.getText());
              this.hasSuccessfullyScanned = true;
              
              // CRITICAL: Immediately hide video and stop everything
              this.immediateVideoHide(video);
              
              this.ngZone.run(() => {
                this.scanning = false;
                this.cameraReady = false;
                
                // Dismiss modal immediately
                if (!this.isDestroyed) {
                  this.modalCtrl.dismiss({ scannedText: result.getText() });
                }
                
                // Cleanup after dismiss (async, non-blocking)
                setTimeout(() => this.completeCleanup(), 0);
              });
              return;
            }
          } catch (decodeError) {
            if (!(decodeError instanceof NotFoundException)) {
              consecutiveFailures++;
              if (consecutiveFailures > this.MAX_CONSECUTIVE_FAILURES) {
                console.error('[QrScanner] Too many consecutive decode failures');
                this.ngZone.run(() => {
                  this.completeCleanup();
                  if (!this.isDestroyed) {
                    this.showInlineError('Scanner encountered repeated errors. Please try again.');
                  }
                });
                return;
              }
            } else {
              consecutiveFailures = 0;
            }
          }
        }
      } catch (err) {
        console.error('[QrScanner] Frame scan error:', err);
      }

      if (!this.isDestroyed) {
        this.animationFrame = requestAnimationFrame(scanFrame);
      }
    };

    this.animationFrame = requestAnimationFrame(scanFrame);
  }

  private immediateVideoHide(video: HTMLVideoElement): void {
    try {
      // Stop stream immediately
      this.stopCurrentStream();
      
      // Stop animation immediately
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }
      
      // Hide video element immediately
      video.pause();
      video.style.opacity = '0';
      video.style.visibility = 'hidden';
      video.style.display = 'none';
      video.removeAttribute('controls');
      video.controls = false;
      
      console.log('[QrScanner] Video hidden immediately');
    } catch (error) {
      console.error('[QrScanner] Error in immediate video hide:', error);
    }
  }

  private stopScanner(): void {
    console.log('[QrScanner] stopScanner called');
    
    // First, stop the scanning state
    this.ngZone.run(() => {
      this.scanning = false;
      this.cameraReady = false;
    });

    // Stop animation frame
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }

    // Reset code reader
    try {
      (this.codeReader as any).reset?.();
    } catch (error) {
      console.error('[QrScanner] Error resetting code reader:', error);
    }

    // Stop all media tracks FIRST
    this.stopCurrentStream();

    // Clean up video element completely
    if (this.videoElement?.nativeElement) {
      const video = this.videoElement.nativeElement;
      
      try {
        this.immediateVideoHide(video);
        
        // Delayed complete cleanup
        setTimeout(() => {
          try {
            video.src = '';
            video.removeAttribute('src');
            video.removeAttribute('autoplay');
            video.poster = '';
            video.load();
          } catch (err) {
            console.error('[QrScanner] Error in delayed cleanup:', err);
          }
        }, 10);
      } catch (error) {
        console.error('[QrScanner] Error cleaning video element:', error);
      }
    }
  }

  private completeCleanup(): void {
    console.log('[QrScanner] completeCleanup called');
    
    this.stopScanner();
    this.hideLoading();
    
    // Additional cleanup for canvas
    if (this.canvas) {
      try {
        this.context?.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.canvas.width = 0;
        this.canvas.height = 0;
      } catch (error) {
        console.error('[QrScanner] Error cleaning canvas:', error);
      }
    }
    
    // Final video element cleanup
    setTimeout(() => {
      if (this.videoElement?.nativeElement) {
        const video = this.videoElement.nativeElement;
        try {
          video.pause();
          video.srcObject = null;
          video.src = '';
          video.load();
          video.style.opacity = '0';
          video.style.display = 'none';
        } catch (err) {
          console.error('[QrScanner] Final video cleanup error:', err);
        }
      }
    }, 100);
  }

  private showInlineError(message: string): void {
    if (this.isDestroyed) return;

    this.errorMessage = message;
    this.cameraReady = false;
    this.scanning = false;
    this.stopScanner();
  }

  private getErrorMessage(error: any): string {
    const errorMessages: Record<string, string> = {
      NotAllowedError:
        'Camera permission denied. Please enable camera access in your device settings.',
      NotFoundError:
        'No camera found. Please ensure your device has a working camera.',
      NotReadableError:
        'Camera is in use by another application. Please close other apps and try again.',
      NotSupportedError:
        'Camera access is not supported in this browser or device.',
      AbortError:
        'Camera access was aborted. Please try again.',
      NotReadable:
        'Camera is already in use by another application.',
    };

    return errorMessages[error.name] || error.message || 'Unable to access camera.';
  }

  private async handlePermissionDenied(): Promise<void> {
    await this.hideLoading();
    this.ngZone.run(() => {
      this.completeCleanup();
      if (!this.isDestroyed) {
        this.modalCtrl.dismiss({
          error: 'Camera permission denied',
          needsPermission: true,
        });
      }
    });
  }

  close(): void {
    console.log('[QrScanner] close() called');
    
    // Immediately hide video to prevent controls flash
    if (this.videoElement?.nativeElement) {
      this.immediateVideoHide(this.videoElement.nativeElement);
    }
    
    this.completeCleanup();
    
    if (!this.isDestroyed) {
      this.modalCtrl.dismiss({ cancelled: true });
    }
  }

  async retry(): Promise<void> {
    if (this.isDestroyed) return;
    
    console.log('[QrScanner] Retrying scanner...');
    this.retryAttempts = 0;
    this.errorMessage = '';
    this.hasSuccessfullyScanned = false;

    // Clean up before retry
    this.stopScanner();

    await this.showLoading();
    try {
      await this.initializeScanner();
    } catch {
      if (!this.isDestroyed) {
        this.showInlineError('Camera initialization failed after retry.');
      }
    } finally {
      await this.hideLoading();
    }
  }

  private async showLoading(): Promise<void> {
    if (!this.loading) {
      this.loading = await this.loadingService.showLoading().toPromise();
    }
  }

  private async hideLoading(): Promise<void> {
    if (this.loading) {
      try {
        await this.loadingService.hideLoading(this.loading);
      } catch (error) {
        console.error('[QrScanner] Error hiding loading:', error);
      }
      this.loading = null;
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}